import { useCallback, useMemo } from "react";
import dayjs from "dayjs";
import { TriangleAlert } from "lucide-react";
import { Layout } from "../../UI/Layout/Layout.tsx";
import { NavBar } from "../../UI/Layout/NavBar.tsx";
import { Footer } from "../../UI/Layout/Footer.tsx";
import { AdminMainContainer } from "../../UI/Admin/AdminMainContainer.tsx";
import { usePurchasedItemNames } from "../../ProductService/PurchasedItem/usePurchasedItemNames.ts";
import { useAllPromos } from "../../ProductService/NormasTeachingPromo/useAllPromos.ts";
import { SaleRecord, SoldItem } from "./SaleRecord.ts";
import {
  bucketsOf,
  comparisonPeriodOf,
  defaultGranularityOf,
  SalesComparison,
  salesPeriodOf,
} from "./salesPeriod.ts";
import {
  B2B_CONTRACTS_ITEM_KEY,
  compareInstallmentsKeys,
  installmentsKeysOf,
  installmentsSeriesKeyOf,
  itemKeyOf,
  matchesSalesFilters,
  paymentMethodKeyOf,
} from "./salesFilters.ts";
import {
  installmentsLabelOf,
  paymentMethodLabelOf,
  SALES_GATEWAY_LABELS,
} from "./salesLabels.ts";
import {
  hasActivityIn,
  ordersPlacedIn,
  salesConcludedIn,
  summariesByBucket,
  summarizeSales,
} from "./salesSummary.ts";
import { PromotionInPeriod, promotionsShownIn } from "./salesPromotions.ts";
import {
  buildSalesTrend,
  SalesTrendSplit,
  salesTrendSeriesOf,
} from "./salesTrend.ts";
import {
  bonificoPaymentsOf,
  couponUsageOf,
  expectedCollectionsOf,
  installmentsHealthOf,
  outcomesByGateway,
  refundedSalesIn,
  refundsByItem,
  salesByItem,
  salesHeatmapOf,
  salesWithCouponIn,
} from "./salesBreakdowns.ts";
import { formatBucket, formatPeriod } from "./salesFormat.ts";
import { useSaleRecords } from "./useSaleRecords.ts";
import { useSalesStatisticsUrlState } from "./useSalesStatisticsUrlState.ts";
import { SalesFilterOption, SalesFiltersBar } from "./SalesFiltersBar.tsx";
import { SalesSummaryTiles } from "./SalesSummaryTiles.tsx";
import { SalesTrendCard } from "./SalesTrendCard.tsx";
import { TopItemsCard } from "./TopItemsCard.tsx";
import { InstallmentsHealthCard } from "./InstallmentsHealthCard.tsx";
import { GatewayOutcomesCard } from "./GatewayOutcomesCard.tsx";
import { BonificoPaymentsCard } from "./BonificoPaymentsCard.tsx";
import { CouponsCard } from "./CouponsCard.tsx";
import { RefundsByItemCard } from "./RefundsByItemCard.tsx";
import { ExpectedCollectionsCard } from "./ExpectedCollectionsCard.tsx";
import { SalesHeatmapCard } from "./SalesHeatmapCard.tsx";

const WITHOUT_ITEM_KEY = "none";
const PROMOTION_ITEMS_LISTED = 3;

const COMPARISON_LABELS: Record<
  SalesComparison,
  { long: string; short: string }
> = {
  [SalesComparison.PreviousPeriod]: {
    long: "Periodo precedente",
    short: "vs prima",
  },
  [SalesComparison.LastYear]: { long: "Anno scorso", short: "vs anno scorso" },
};

function distinctOptions(
  keys: string[],
  labelOf: (key: string) => string,
  compare: (first: SalesFilterOption, second: SalesFilterOption) => number,
): SalesFilterOption[] {
  return [...new Set(keys)]
    .map((key) => ({ id: key, label: labelOf(key) }))
    .sort(compare);
}

function byLabel(first: SalesFilterOption, second: SalesFilterOption): number {
  return first.label.localeCompare(second.label, "it");
}

function byNumberOfInstallments(
  first: SalesFilterOption,
  second: SalesFilterOption,
): number {
  return compareInstallmentsKeys(first.id, second.id);
}

function SectionHeading({ title }: { title: string }) {
  return (
    <h2 className="mt-2 text-lg font-semibold text-gray-700 border-b border-gray-200 pb-1">
      {title}
    </h2>
  );
}

function soldItemsByKey(records: SaleRecord[]): Map<string, SoldItem> {
  const items = new Map<string, SoldItem>();
  for (const record of records) {
    const itemKey = itemKeyOf(record);
    if (!itemKey || !record.item) continue;
    if (!items.get(itemKey)?.name) items.set(itemKey, record.item);
  }
  return items;
}

export function SalesStatisticsScreen() {
  const today = useMemo(() => dayjs(), []);
  const { view, changeView } = useSalesStatisticsUrlState();
  const period = useMemo(
    () =>
      salesPeriodOf({
        preset: view.preset,
        customFrom: view.customFrom,
        customTo: view.customTo,
        today,
      }),
    [view.preset, view.customFrom, view.customTo, today],
  );
  const previousPeriod = useMemo(
    () =>
      comparisonPeriodOf({
        period,
        preset: view.preset,
        comparison: view.comparison,
      }),
    [period, view.preset, view.comparison],
  );
  const comparisonLabels = COMPARISON_LABELS[view.comparison];
  const fetchedRange = useMemo(
    () => ({ from: previousPeriod.from, to: period.to }),
    [previousPeriod, period],
  );
  const { records, failedGateways, isLoading } = useSaleRecords(fetchedRange);
  const { resolveName } = usePurchasedItemNames();
  const { promos } = useAllPromos();

  const itemsByKey = useMemo(() => soldItemsByKey(records), [records]);
  const itemLabelOf = useCallback(
    (itemKey: string): string => {
      if (itemKey === B2B_CONTRACTS_ITEM_KEY) return "Contratti B2B";
      if (itemKey === WITHOUT_ITEM_KEY) return "Senza prodotto";
      const item = itemsByKey.get(itemKey);
      if (!item) return itemKey;
      return item.name ?? resolveName({ id: item.id, type: item.type }).name;
    },
    [itemsByKey, resolveName],
  );

  const filteredRecords = useMemo(
    () => records.filter((record) => matchesSalesFilters(record, view.filters)),
    [records, view.filters],
  );
  const hasComparisonData = useMemo(
    () => hasActivityIn(filteredRecords, previousPeriod),
    [filteredRecords, previousPeriod],
  );

  const promotions = useMemo(
    () =>
      promotionsShownIn({
        promos,
        period,
        filteredItemKeys: view.filters.items,
      }),
    [promos, period, view.filters.items],
  );
  const describePromotionItems = useCallback(
    (promotion: PromotionInPeriod): string => {
      if (promotion.appliesToEveryItem) return "Tutti i prodotti";
      const names = (promotion.promo.applicableProducts ?? []).map((product) =>
        itemsByKey.has(product.id)
          ? itemLabelOf(product.id)
          : resolveName({ id: product.id, type: product.itemType }).name,
      );
      return names.length > PROMOTION_ITEMS_LISTED
        ? `${names.slice(0, PROMOTION_ITEMS_LISTED).join(", ")} e altri ${names.length - PROMOTION_ITEMS_LISTED}`
        : names.join(", ");
    },
    [itemsByKey, itemLabelOf, resolveName],
  );

  const granularity = view.granularity ?? defaultGranularityOf(period);
  const seriesKeyOf = useMemo(
    () =>
      ({
        [SalesTrendSplit.Gateway]: (record: SaleRecord) => record.gateway,
        [SalesTrendSplit.PaymentMethod]: paymentMethodKeyOf,
        [SalesTrendSplit.Item]: (record: SaleRecord) =>
          itemKeyOf(record) ?? WITHOUT_ITEM_KEY,
        [SalesTrendSplit.Installments]: (record: SaleRecord) =>
          installmentsSeriesKeyOf(record, {
            separatesHeyLightTerms: view.separatesHeyLightTerms,
          }),
      })[view.split],
    [view.split, view.separatesHeyLightTerms],
  );
  const seriesLabelOf = useMemo(
    () =>
      ({
        [SalesTrendSplit.Gateway]: (key: string) =>
          SALES_GATEWAY_LABELS[key as keyof typeof SALES_GATEWAY_LABELS],
        [SalesTrendSplit.PaymentMethod]: paymentMethodLabelOf,
        [SalesTrendSplit.Item]: itemLabelOf,
        [SalesTrendSplit.Installments]: installmentsLabelOf,
      })[view.split],
    [view.split, itemLabelOf],
  );

  const trendSeries = useMemo(
    () =>
      salesTrendSeriesOf({
        split: view.split,
        recordsOfThePeriod: records,
        metric: view.metric,
        period,
        seriesKeyOf,
        seriesLabelOf,
      }),
    [records, view.split, view.metric, period, seriesKeyOf, seriesLabelOf],
  );
  const [trend, previousTrend] = useMemo(
    () =>
      [period, previousPeriod].map((trendPeriod) =>
        buildSalesTrend({
          records: filteredRecords,
          series: trendSeries,
          seriesKeyOf,
          metric: view.metric,
          period: trendPeriod,
          granularity,
        }),
      ),
    [
      filteredRecords,
      trendSeries,
      seriesKeyOf,
      view.metric,
      period,
      previousPeriod,
      granularity,
    ],
  );

  const statistics = useMemo(
    () => ({
      current: summarizeSales(filteredRecords, period),
      previous: hasComparisonData
        ? summarizeSales(filteredRecords, previousPeriod)
        : null,
      summaryBuckets: summariesByBucket(
        filteredRecords,
        bucketsOf(period, granularity),
      ).map(({ bucket, summary }) => ({
        label: formatBucket(bucket, granularity),
        summary,
      })),
      items: salesByItem(filteredRecords, period),
      concludedSales: salesConcludedIn(filteredRecords, period),
      previousSoldByItem: hasComparisonData
        ? new Map(
            salesByItem(filteredRecords, previousPeriod).map((item) => [
              item.itemKey,
              item.soldInCents,
            ]),
          )
        : null,
      installmentsHealth: installmentsHealthOf(filteredRecords, period),
      expectedCollections: expectedCollectionsOf(filteredRecords, today),
      heatmap: salesHeatmapOf(filteredRecords, period),
      outcomes: outcomesByGateway(filteredRecords, period),
      ordersPlaced: ordersPlacedIn(filteredRecords, period),
      bonificoPayments: bonificoPaymentsOf(filteredRecords, period),
      couponUsage: couponUsageOf(filteredRecords, period),
      salesWithCoupon: salesWithCouponIn(filteredRecords, period),
      refunds: refundsByItem(filteredRecords, period),
      refundedSales: refundedSalesIn(filteredRecords, period),
    }),
    [
      filteredRecords,
      period,
      previousPeriod,
      hasComparisonData,
      granularity,
      today,
    ],
  );

  const filterOptions = useMemo(
    () => ({
      paymentMethods: distinctOptions(
        records.map(paymentMethodKeyOf),
        paymentMethodLabelOf,
        byLabel,
      ),
      installments: distinctOptions(
        records.flatMap(installmentsKeysOf),
        installmentsLabelOf,
        byNumberOfInstallments,
      ),
    }),
    [records],
  );

  return (
    <Layout>
      <AdminMainContainer>
        <NavBar showAdminSideBarHandle />
        <div className="flex-1 p-4 flex flex-col gap-4 max-w-full min-w-0">
          <div className="flex flex-col gap-1">
            <h1 className="font-bold text-3xl">Statistiche vendite</h1>
            <p className="text-sm text-gray-500">
              {formatPeriod(period)} · confronto con{" "}
              {formatPeriod(previousPeriod)}
              {!isLoading &&
                !hasComparisonData &&
                " (nessun dato da confrontare)"}
            </p>
          </div>
          <div className="md:sticky md:top-0 md:z-30 -mx-4 px-4 py-2 bg-slate-100/95 backdrop-blur-sm">
            <SalesFiltersBar
              preset={view.preset}
              period={period}
              comparison={view.comparison}
              filters={view.filters}
              paymentMethodOptions={filterOptions.paymentMethods}
              installmentsOptions={filterOptions.installments}
              itemLabelOf={itemLabelOf}
              onChangePeriod={(change) =>
                changeView({ ...change, granularity: undefined })
              }
              onChangeFilters={(filters) => changeView({ filters })}
            />
          </div>
          {failedGateways.length > 0 && (
            <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
              <TriangleAlert className="w-4 h-4 mt-0.5 shrink-0" aria-hidden />
              <span>
                Non è stato possibile caricare gli ordini di{" "}
                {failedGateways
                  .map((gateway) => SALES_GATEWAY_LABELS[gateway])
                  .join(" e ")}
                : i numeri qui sotto non li comprendono.
              </span>
            </div>
          )}
          <SectionHeading title="Vendite" />
          <SalesSummaryTiles
            current={statistics.current}
            previous={statistics.previous}
            previousPeriodLabel={formatPeriod(previousPeriod)}
            comparisonShortLabel={comparisonLabels.short}
            buckets={statistics.summaryBuckets}
            isLoading={isLoading}
          />
          <SalesTrendCard
            trend={trend}
            previousBuckets={hasComparisonData ? previousTrend.buckets : []}
            comparisonLabel={comparisonLabels.long}
            promotions={promotions}
            describePromotionItems={describePromotionItems}
            metric={view.metric}
            split={view.split}
            granularity={granularity}
            period={period}
            isLoading={isLoading}
            onChangeMetric={(metric) => changeView({ metric })}
            onChangeSplit={(split) => changeView({ split })}
            separatesHeyLightTerms={view.separatesHeyLightTerms}
            onChangeSeparatesHeyLightTerms={(separatesHeyLightTerms) =>
              changeView({ separatesHeyLightTerms })
            }
            onChangeGranularity={(selected) =>
              changeView({ granularity: selected })
            }
          />
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            <TopItemsCard
              items={statistics.items}
              sales={statistics.concludedSales}
              previousSoldByItem={statistics.previousSoldByItem}
              comparisonShortLabel={comparisonLabels.short}
              itemLabelOf={itemLabelOf}
              period={period}
              isLoading={isLoading}
            />
            <GatewayOutcomesCard
              outcomes={statistics.outcomes}
              orders={statistics.ordersPlaced}
              itemLabelOf={itemLabelOf}
              period={period}
              isLoading={isLoading}
            />
          </div>
          <SalesHeatmapCard
            cells={statistics.heatmap}
            period={period}
            isLoading={isLoading}
          />
          <SectionHeading title="Incassi e rate" />
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            <ExpectedCollectionsCard
              months={statistics.expectedCollections}
              period={period}
              isLoading={isLoading}
            />
            <InstallmentsHealthCard
              health={statistics.installmentsHealth}
              isLoading={isLoading}
            />
          </div>
          <BonificoPaymentsCard
            payments={statistics.bonificoPayments}
            isLoading={isLoading}
          />
          <SectionHeading title="Promozioni e rimborsi" />
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            <CouponsCard
              usage={statistics.couponUsage}
              salesWithCoupon={statistics.salesWithCoupon}
              itemLabelOf={itemLabelOf}
              period={period}
              isLoading={isLoading}
            />
            <RefundsByItemCard
              refunds={statistics.refunds}
              refundedSales={statistics.refundedSales}
              itemLabelOf={itemLabelOf}
              period={period}
              isLoading={isLoading}
            />
          </div>
        </div>
      </AdminMainContainer>
      <Footer />
    </Layout>
  );
}
